<template>
  <v-card class="mt-4 ml-2 mr-2" elevation="6">
    <v-card-item>
      <template #prepend>
        <v-avatar color="primary" variant="tonal" size="48">
          <v-icon>mdi-crown</v-icon>
        </v-avatar>
      </template>
      <v-card-title>Membre Premium</v-card-title>
      <v-card-subtitle class="text-wrap">{{ subtitle }}</v-card-subtitle>
      <template #append>
        <v-chip :color="status.color" variant="flat" size="small" label>{{ status.label }}</v-chip>
      </template>
    </v-card-item>

    <v-divider></v-divider>

    <v-list v-if="details.length" lines="two" density="comfortable">
      <v-list-item
        v-for="detail in details"
        :key="detail.title"
        :prepend-icon="detail.icon"
        :title="detail.title"
        :subtitle="detail.value"
      ></v-list-item>
    </v-list>

    <v-card-text>
      <v-alert :type="notice.type" variant="tonal">
        <div class="font-weight-bold mb-1">{{ notice.title }}</div>
        {{ notice.text }}
      </v-alert>
    </v-card-text>

    <template v-if="subscription">
      <v-card-actions class="px-4">
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-open-in-new"
          :loading="isLoading"
          @click="openCustomerPortal"
        >
          Ouvrir mon portail d'abonnement
        </v-btn>
      </v-card-actions>
      <v-card-text class="pt-2 text-body-small">
        Le portail sécurisé de Stripe, notre partenaire de paiement, vous permet de mettre à jour votre carte,
        de consulter vos factures et d'annuler votre abonnement.
      </v-card-text>
    </template>
  </v-card>
</template>

<script>
  import { functions } from "@/firebase";
  import { formatLongDate } from "@/utils/dates";
  import { notifyError } from "@/services/Notification.service";

  // Membership card: the Stripe subscription (renewal, cancellation, late payment) and its portal, or the
  // membership without a subscription (one-time payment, admin).
  export default {
    name: "SubscribedAccount",
    props: {
      // { id, status, cancel_at_period_end, current_period_end } from stripeGetActiveSubscription, or null
      subscription: {
        type: Object,
        default: null,
      },
      membershipEndDate: {
        type: Number,
        default: null,
      },
      isAdmin: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isLoading: false,
      };
    },
    computed: {
      isCancelled() {
        return Boolean(this.subscription && this.subscription.cancel_at_period_end);
      },
      hasPaymentIssue() {
        return Boolean(this.subscription && ["past_due", "unpaid"].includes(this.subscription.status));
      },
      // Last day of access: end of the subscription period, or of the membership without subscription
      endDate() {
        const seconds = this.subscription ? this.subscription.current_period_end : this.membershipEndDate;
        return formatLongDate(seconds);
      },
      subtitle() {
        if (!this.subscription) {
          return this.isAdmin ? "Accès inclus avec votre compte administrateur" : "Accès Premium sans renouvellement";
        }
        return this.isCancelled ? "Abonnement annuel, non renouvelé" : "Abonnement annuel, renouvelé automatiquement";
      },
      status() {
        if (this.isAdmin && !this.subscription) {
          return { label: "Administrateur", color: "error" };
        }
        if (this.hasPaymentIssue) {
          return { label: "Paiement en retard", color: "warning" };
        }
        if (this.isCancelled) {
          return { label: "Annulé", color: "grey" };
        }
        return { label: "Actif", color: "success" };
      },
      details() {
        if (!this.endDate) {
          return [];
        }
        if (!this.subscription) {
          return [{ icon: "mdi-calendar-check", title: "Accès Premium jusqu'au", value: this.endDate }];
        }
        return [
          this.isCancelled
            ? { icon: "mdi-calendar-remove", title: "Fin de l'abonnement", value: this.endDate }
            : { icon: "mdi-calendar-sync", title: "Prochain renouvellement", value: this.endDate },
        ];
      },
      notice() {
        const until = this.endDate ? `jusqu'au ${this.endDate}` : "jusqu'à la fin de la période en cours";
        if (!this.subscription) {
          return this.isAdmin
            ? {
                type: "info",
                title: "Compte administrateur",
                text: "Vous êtes connecté en tant qu'administrateur : l'accès Premium est inclus, sans paiement.",
              }
            : {
                type: "info",
                title: "Aucun renouvellement automatique",
                text: `Votre accès Premium reste actif ${until}. Aucun paiement ne sera prélevé par la suite.`,
              };
        }
        if (this.hasPaymentIssue) {
          return {
            type: "warning",
            title: "Le dernier paiement n'a pas fonctionné",
            text: "Ouvrez votre portail d'abonnement pour mettre à jour votre carte et garder votre accès Premium.",
          };
        }
        if (this.isCancelled) {
          return {
            type: "info",
            title: "Abonnement annulé",
            text: `Votre abonnement ne sera pas renouvelé. Il reste actif ${until}.`,
          };
        }
        return {
          type: "info",
          title: "Pour éviter le renouvellement automatique",
          text:
            "Ouvrez votre portail d'abonnement et cliquez sur « Annuler l'abonnement ». " +
            `Votre abonnement restera actif ${until} et ne sera pas renouvelé.`,
        };
      },
    },
    methods: {
      async openCustomerPortal() {
        this.isLoading = true;
        try {
          const createPortalSession = functions.httpsCallable("stripeCreatePortalSession");
          const { data } = await createPortalSession({ returnUrl: window.location.origin });
          window.location.assign(data.url);
        } catch (error) {
          console.error("Unable to open the Stripe portal:", error);
          notifyError("Impossible d'ouvrir le portail d'abonnement. Veuillez réessayer.");
          this.isLoading = false;
        }
      },
    },
  };
</script>
